import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Search, UserSearch } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Input } from '@/components/Input';
import { PageHeader } from '@/components/PageHeader';
import { UserList } from '@/components/community/UserRow';
import { userSearchSchema } from '@/components/community/searchParams';
import { useUserSearch } from '@/lib/queries/users';

/** Sin escribir nada durante este rato no se pide nada al servidor. */
const DEBOUNCE_MS = 300;

export const Route = createFileRoute('/_app/community/search')({
  validateSearch: userSearchSchema,
  component: UserSearchPage,
});

/**
 * Búsqueda de cuentas.
 *
 * El texto vive en la URL (`?q=`) para poder volver atrás desde un perfil y
 * encontrar la misma búsqueda, pero sólo se escribe pasado el `debounce`: si
 * no, cada tecla dejaría un paso en el historial.
 */
function UserSearchPage() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [text, setText] = useState(q ?? '');

  const latest = useRef(navigate);
  latest.current = navigate;
  const committed = useRef(q ?? '');

  useEffect(() => {
    const external = q ?? '';
    if (external !== committed.current) {
      committed.current = external;
      setText(external);
    }
  }, [q]);

  useEffect(() => {
    if (text.trim() === committed.current) return;
    const id = window.setTimeout(() => {
      const trimmed = text.trim();
      committed.current = trimmed;
      void latest.current({
        search: trimmed === '' ? {} : { q: trimmed },
        replace: true,
      });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [text]);

  const term = (q ?? '').trim();
  const query = useUserSearch(term);
  const users = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <PageHeader
        title="Buscar cuentas"
        subtitle="Escribe un nombre o un @handle."
        action={
          <Link
            to="/community"
            search={{}}
            aria-label="Volver a la comunidad"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="relative mb-4">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-faint"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          aria-label="Buscar cuentas"
          placeholder="laura, @carlos_ruiz…"
          className="pl-9"
        />
      </div>

      {term.length < 2 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line bg-surface-raised px-5 py-10 text-center">
          <UserSearch className="size-8 text-ink-faint" aria-hidden="true" />
          <p className="text-sm text-ink-muted">
            Escribe al menos dos letras para buscar. Sólo aparecen las cuentas públicas.
          </p>
        </div>
      ) : (
        <UserList
          users={users}
          isPending={query.isPending}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={() => void query.fetchNextPage()}
          errorTitle="No hemos podido buscar"
          loadingLabel="Buscando…"
          empty={
            <p className="rounded-2xl border border-dashed border-line bg-surface-raised px-5 py-10 text-center text-sm text-ink-muted">
              Nadie coincide con «{term}».
            </p>
          }
        />
      )}
    </>
  );
}
